import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { signOut } from 'firebase/auth';
import { auth } from '../../firebase';
import { useAuth } from '../../context/AuthContext';
import { Spinner } from '../ui';
import {
  fetchConsentSummaries,
  needsConsent,
  buildFirstLoginConsentEvents,
  recordConsentEvents,
} from '../../services/consentService';
import { REQUIRED_CONSENT_TYPES } from '../../types/consent';
import {
  PRIVACY_NOTICE_VERSION,
  PRIVACY_NOTICE_DATE,
  TERMS_VERSION,
} from '../../config/legalDocuments';

/**
 * Blocking consent gate on first entry to the member app.
 *
 * Members can reach the app without ever passing through /enrollment — the
 * cohort seeder provisions accounts directly — so consent could not be assumed
 * to have been captured. This is the backstop: no member sees their record
 * until the required consents exist against the CURRENT document version.
 *
 * Scope: REQUIRED consents only, and the set is read from REQUIRED_CONSENT_TYPES
 * rather than restated here. It used to be restated, and it drifted: when
 * `genetics_context` was moved from optional to required (Consent §10.2 permits a
 * mandatory element, and both capture surfaces already treated it as one), this
 * gate carried on rendering three checkboxes while buildFirstLoginConsentEvents —
 * which maps over REQUIRED_CONSENT_TYPES — began writing four. Clicking "Agree
 * and continue" recorded a granted consent the member had never been shown, which
 * is the precise thing Consent §4.2 forbids.
 *
 * So the tick list is now DERIVED. Adding a required consent without giving it
 * copy here fails a test rather than silently fabricating a record.
 *
 * `genetic_data_retention` stays out: it is the one genuinely optional consent,
 * refusing it must never block access, and it is put to the member at enrolment
 * and on the consent screen where it can be changed.
 *
 * Design constraints (deliberate, not styling choices):
 *  - Nothing is pre-ticked. A pre-ticked box is not consent.
 *  - Each item is its own checkbox — no single "I agree to everything".
 *  - Document links open in a new tab so reading them does not discard progress.
 *  - Re-prompts when ONBOARDING_CONSENT_VERSION changes, so a material rewrite
 *    of the terms is re-agreed rather than silently inherited.
 */

const OXBLOOD = '#5C1A1C';

export default function FirstLoginConsentGate({ children }: { children: React.ReactNode }) {
  const { currentUser } = useAuth();
  const uid = currentUser?.uid;

  const [checking, setChecking] = useState(true);
  const [required, setRequired] = useState(false);
  const [readTerms, setReadTerms] = useState(false);
  const [readPrivacy, setReadPrivacy] = useState(false);
  const [healthProcessing, setHealthProcessing] = useState(false);
  const [geneticsContext, setGeneticsContext] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    if (!uid) {
      setChecking(false);
      return undefined;
    }
    (async () => {
      try {
        const summaries = await fetchConsentSummaries(uid);
        if (!cancelled) setRequired(needsConsent(summaries));
      } catch {
        // Fail CLOSED: if we cannot prove consent exists, ask for it. The cost of
        // an extra prompt is far lower than showing a record we have no recorded
        // basis to process.
        if (!cancelled) setRequired(true);
      } finally {
        if (!cancelled) setChecking(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [uid]);

  // Every REQUIRED consent must have a ticked box. Derived, so a type added to
  // REQUIRED_CONSENT_TYPES cannot be silently recorded without being asked.
  const ticks: Record<string, boolean> = {
    terms: readTerms,
    privacy: readPrivacy,
    health_data_processing: healthProcessing,
    genetics_context: geneticsContext,
  };
  const allRequiredTicked = REQUIRED_CONSENT_TYPES.every((type) => ticks[type] === true);

  const handleSubmit = useCallback(async () => {
    if (!allRequiredTicked || saving) return;
    setSaving(true);
    setError(null);
    try {
      await recordConsentEvents({ events: buildFirstLoginConsentEvents() });
      setRequired(false);
    } catch (err) {
      setError(
        err instanceof Error
          ? `We could not record your consent (${err.message}). Please try again.`
          : 'We could not record your consent. Please try again.',
      );
      setSaving(false);
    }
  }, [allRequiredTicked, saving]);

  if (checking) {
    return (
      <div className="min-h-screen flex items-center justify-center gh-surface">
        <Spinner size="lg" />
      </div>
    );
  }

  if (!required) return <>{children}</>;

  return (
    <div
      className="min-h-screen px-6 py-12 md:py-16"
      style={{ backgroundColor: 'var(--gh-bone, #f6f1ea)' }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="consent-gate-heading"
    >
      <div className="mx-auto" style={{ maxWidth: 640 }}>
        <p className="gh-eyebrow mb-4">Before we begin</p>
        <h1
          id="consent-gate-heading"
          className="gh-serif"
          style={{
            fontSize: 'clamp(1.75rem, 3.5vw, 2.5rem)',
            lineHeight: 1.15,
            margin: 0,
            color: 'var(--gh-graphite)',
          }}
        >
          Your agreement, <em style={{ fontStyle: 'italic' }}>in writing.</em>
        </h1>
        <p className="mt-5" style={{ color: 'var(--gh-ash)', lineHeight: 1.65 }}>
          Melio holds health and genetic information about you, so we ask you to
          confirm a few things before you see your record. Please read both
          documents — they open in a new tab, and nothing here will be lost.
        </p>

        {/* ── Required ─────────────────────────────────────────────── */}
        <div
          className="mt-10 pt-8"
          style={{ borderTop: '1px solid var(--gh-rule, rgba(92, 26, 28,0.15))' }}
        >
          <p className="gh-eyebrow mb-5">Required</p>

          <CheckRow
            id="consent-terms"
            checked={readTerms}
            onChange={setReadTerms}
            label={
              <>
                I have read and agree to the{' '}
                <Link to="/terms" target="_blank" rel="noreferrer" className="gh-link">
                  Terms of Service
                </Link>{' '}
                (version {TERMS_VERSION}).
              </>
            }
          />

          <CheckRow
            id="consent-privacy"
            checked={readPrivacy}
            onChange={setReadPrivacy}
            label={
              <>
                I have read the{' '}
                <Link to="/privacy" target="_blank" rel="noreferrer" className="gh-link">
                  Privacy Notice
                </Link>{' '}
                (version {PRIVACY_NOTICE_VERSION}, {PRIVACY_NOTICE_DATE}).
              </>
            }
          />

          <CheckRow
            id="consent-health"
            checked={healthProcessing}
            onChange={setHealthProcessing}
            label={
              <>
                I explicitly consent to Melio processing my health and genetic
                information to provide and manage my care.
              </>
            }
            detail="This is the specific permission UK data protection law requires for this kind of sensitive data. Without it we cannot deliver the service."
          />

          <CheckRow
            id="consent-genetics-context"
            checked={geneticsContext}
            onChange={setGeneticsContext}
            label={
              <>
                I understand Melio&rsquo;s opening genetics layer is a context-only
                SNP / preventive test, not a definitive diagnostic genetics service.
              </>
            }
            detail="Where needed, Melio may recommend confirmatory testing through an accredited clinical laboratory. This is the same confirmation given at enrolment; it is asked again because it is required for the service."
          />
        </div>

        {error && (
          <p
            className="mt-6 text-sm"
            role="alert"
            style={{ color: OXBLOOD, fontWeight: 500 }}
          >
            {error}
          </p>
        )}

        <div className="mt-10 flex flex-wrap items-center gap-4">
          <button
            type="button"
            onClick={handleSubmit}
            disabled={!allRequiredTicked || saving}
            className="gh-btn-primary"
            style={{
              minWidth: 200,
              opacity: !allRequiredTicked || saving ? 0.45 : 1,
              cursor: !allRequiredTicked || saving ? 'not-allowed' : 'pointer',
            }}
          >
            {saving ? 'Saving…' : 'Agree and continue'}
          </button>

          {/* No dark pattern: declining is a visible, working option. It cannot
              proceed into the app, so the honest outcome is to sign out. */}
          <button
            type="button"
            onClick={() => signOut(auth)}
            style={{
              background: 'none',
              border: 'none',
              padding: 0,
              cursor: 'pointer',
              color: 'var(--gh-ash)',
              fontSize: '0.875rem',
              textDecoration: 'underline',
            }}
          >
            I do not agree — sign out
          </button>
        </div>

        <p className="mt-8 text-xs" style={{ color: 'var(--gh-ash)' }}>
          Questions about your data? Email{' '}
          <a href="mailto:help@meliotherapeutics.uk" className="gh-link">
            help@meliotherapeutics.uk
          </a>
          .
        </p>
      </div>
    </div>
  );
}

function CheckRow({
  id,
  checked,
  onChange,
  label,
  detail,
}: {
  id: string;
  checked: boolean;
  onChange: (next: boolean) => void;
  label: React.ReactNode;
  detail?: string;
}) {
  return (
    <div className="mb-6 flex items-start gap-3">
      <input
        id={id}
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        style={{ marginTop: 4, width: 18, height: 18, accentColor: OXBLOOD, flexShrink: 0 }}
      />
      <label htmlFor={id} style={{ cursor: 'pointer' }}>
        <span style={{ color: 'var(--gh-graphite)', lineHeight: 1.55 }}>{label}</span>
        {detail && (
          <span
            className="mt-1 block text-sm"
            style={{ color: 'var(--gh-ash)', lineHeight: 1.55 }}
          >
            {detail}
          </span>
        )}
      </label>
    </div>
  );
}
